import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "packages/dsaui/src/utils";

export interface CardProps extends ComponentPropsWithoutRef<"section"> {
  children: ReactNode;
}

export const Card = ({ children, className, ...props }: CardProps) => {
  const classes = className
    ? `rounded-lg border border-gray-200 bg-white p-6 shadow-sm ${className}`
    : "rounded-lg border border-gray-200 bg-white p-6 shadow-sm";

  return (
    <section className={classes} {...props}>
      {children}
    </section>
  );
};

export const CardHeader = ({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<"div">) => {
  const classes = className
    ? `flex flex-col gap-4 ${className}`
    : "flex flex-col gap-4";

  return (
    <div className={classes} {...props}>
      {children}
    </div>
  );
};

export const CardTitle = ({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<"h2">) => {
  const classes = className
    ? `font-inter m-0 text-lg leading-6 font-medium text-gray-900 ${className}`
    : "font-inter m-0 text-lg leading-6 font-medium text-gray-900";

  return (
    <h2 className={classes} {...props}>
      {children}
    </h2>
  );
};

export const CardBody = ({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<"div">) => {
  const classes = cn("flex flex-col gap-5", className);

  return (
    <div className={classes} {...props}>
      {children}
    </div>
  );
};

export const CardFooter = ({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<"div">) => {
  const classes = className ? `mt-auto pt-1 ${className}` : "mt-auto pt-1";

  return (
    <div className={classes} {...props}>
      {children}
    </div>
  );
};

export const CardSkeleton = ({
  className,
  footerFullWidth = true,
  leadingBullets = true,
  rows = 3,
  showBanner = true,
  showFooter = true,
  ...props
}: Omit<ComponentPropsWithoutRef<"div">, "children"> & {
  footerFullWidth?: boolean;
  leadingBullets?: boolean;
  rows?: number;
  showBanner?: boolean;
  showFooter?: boolean;
}) => {
  const { t } = useTranslation();
  const classes = className ? `animate-pulse ${className}` : "animate-pulse";
  const footerSkeletonClasses = cn(
    "h-11 rounded-md border border-gray-100",
    footerFullWidth ? "w-full bg-gray-300" : "w-24 bg-gray-200",
  );

  return (
    <Card
      aria-busy="true"
      aria-label={t("dashboard.loading.card")}
      className={classes}
      {...props}
    >
      <CardBody className="h-full gap-6">
        <div className="flex flex-col gap-4">
          <div className="size-14 rounded-lg bg-gray-100" />
          <div className="h-4 w-40 rounded-md bg-gray-400" />
        </div>

        <div className="flex flex-col gap-3">
          {Array.from({ length: rows }).map((_, index) => (
            <div key={index} className="flex items-start gap-3">
              {leadingBullets && (
                <div className="mt-0.5 size-4 rounded-full bg-gray-200" />
              )}
              <div className="flex-1 space-y-2">
                <div className="h-4 w-full rounded-md bg-gray-300" />
                <div className="h-4 w-2/3 rounded-md bg-gray-300" />
              </div>
            </div>
          ))}
        </div>

        {showBanner ? (
          <div className="rounded-md bg-gray-100 p-4">
            <div className="space-y-2">
              <div className="h-4 w-full rounded-md bg-gray-300" />
              <div className="h-4 w-5/6 rounded-md bg-gray-300" />
            </div>
          </div>
        ) : null}

        {showFooter ? (
          <CardFooter className="pt-0">
            <div className={footerSkeletonClasses} />
          </CardFooter>
        ) : null}
      </CardBody>
    </Card>
  );
};

export default Card;
